export const TABLE_COLUMN_MIN_WIDTH = 72;
export const TABLE_COLUMN_MAX_WIDTH = 2400;
export const TABLE_COLUMN_DEFAULT_WIDTH = 140;
export const TABLE_PREFERENCE_VERSION = 2;

export const getColumnPreferenceKey = (column, index) => `${column?.key || column?.accessor || `column-${index}`}`;

export const isBuiltInIndexColumn = (column) => {
  if (typeof column?.header !== 'string') return false;
  const normalizedHeader = column.header.replace(/\s/g, '').toUpperCase();
  return normalizedHeader === 'STT' || normalizedHeader === '#NO';
};

export const getDataTableColumns = (columns = []) => columns.filter((column) => !isBuiltInIndexColumn(column));

const getDefaultColumnWidth = (column) => {
  const configuredWidth = Number(column?.defaultWidth || column?.styleHeader?.width || column?.style?.width);
  return Number.isFinite(configuredWidth) ? configuredWidth : TABLE_COLUMN_DEFAULT_WIDTH;
};

export const clampTableColumnWidth = (column, value) => {
  const minWidth = Number(column?.minWidth || column?.styleHeader?.minWidth || TABLE_COLUMN_MIN_WIDTH);
  const maxWidth = Number(column?.maxWidth || TABLE_COLUMN_MAX_WIDTH);
  const numericValue = Number(value);
  const width = Number.isFinite(numericValue) ? numericValue : getDefaultColumnWidth(column);
  return Math.round(Math.min(maxWidth, Math.max(minWidth, width)));
};

export const getDefaultTablePreferences = (columns = []) => ({
  version: TABLE_PREFERENCE_VERSION,
  hiddenKeys: [],
  resizedKeys: [],
  widths: Object.fromEntries(
    columns.map((column, index) => [
      getColumnPreferenceKey(column, index),
      clampTableColumnWidth(column, getDefaultColumnWidth(column)),
    ]),
  ),
});

export const normalizeTablePreferences = (columns = [], value = {}) => {
  const defaults = getDefaultTablePreferences(columns);
  const validKeys = new Set(columns.map(getColumnPreferenceKey));
  const lockedVisibleKeys = new Set(
    columns
      .map((column, index) => ({ column, key: getColumnPreferenceKey(column, index) }))
      .filter(({ column }) => column?.lockedVisible)
      .map(({ key }) => key),
  );
  return {
    version: TABLE_PREFERENCE_VERSION,
    hiddenKeys: Array.isArray(value?.hiddenKeys)
      ? value.hiddenKeys.filter((key) => validKeys.has(key) && !lockedVisibleKeys.has(key))
      : [],
    resizedKeys: Array.isArray(value?.resizedKeys)
      ? value.resizedKeys.filter((key) => validKeys.has(key))
      : [],
    widths: Object.fromEntries(
      columns.map((column, index) => {
        const key = getColumnPreferenceKey(column, index);
        return [key, clampTableColumnWidth(column, value?.widths?.[key] ?? defaults.widths[key])];
      }),
    ),
  };
};

export const loadTablePreferences = (storage, storageKey, columns = []) => {
  if (!storage || !storageKey) return getDefaultTablePreferences(columns);
  try {
    const rawValue = storage.getItem(storageKey);
    return normalizeTablePreferences(columns, rawValue ? JSON.parse(rawValue) : {});
  } catch (_) {
    return getDefaultTablePreferences(columns);
  }
};

export const saveTablePreferences = (storage, storageKey, columns, preferences) => {
  if (!storage || !storageKey) return;
  storage.setItem(storageKey, JSON.stringify(normalizeTablePreferences(columns, preferences)));
};

export const setTableColumnWidth = (columns, preferences, key, width) => {
  const columnIndex = columns.findIndex((column, index) => getColumnPreferenceKey(column, index) === key);
  if (columnIndex < 0) return normalizeTablePreferences(columns, preferences);
  const normalized = normalizeTablePreferences(columns, preferences);
  return {
    ...normalized,
    resizedKeys: [...new Set([...normalized.resizedKeys, key])],
    widths: {
      ...normalized.widths,
      [key]: clampTableColumnWidth(columns[columnIndex], width),
    },
  };
};

export const toggleTableColumn = (columns, preferences, key) => {
  const normalized = normalizeTablePreferences(columns, preferences);
  const targetColumn = columns.find((column, index) => getColumnPreferenceKey(column, index) === key);
  if (!targetColumn || targetColumn.lockedVisible) {
    return normalized;
  }
  const hiddenKeys = new Set(normalized.hiddenKeys);
  if (hiddenKeys.has(key)) {
    hiddenKeys.delete(key);
  } else if (columns.filter((column, index) => !column.hidden && !hiddenKeys.has(getColumnPreferenceKey(column, index))).length > 1) {
    hiddenKeys.add(key);
  }
  return normalizeTablePreferences(columns, { ...normalized, hiddenKeys: [...hiddenKeys] });
};
